import React, { useCallback, useEffect } from 'react';
import styles from './styles';
import {
    Typography,
    Container,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from "@mui/icons-material/Search";
import Header from '../Header/Header';
import siteManagementService from '../../services/SiteManagementService';
import AddModal from './Modals/AddModal';
import EditModal from './Modals/EditModal';
import DeleteModal from './Modals/DeleteModal';
import { LocationDTO, LPLItemDTO } from '@beable/roster-backend-client-v1';
import BasePanel from '../Common/Layout/BasePanel';
import { BasePanelBox } from '../Common/Layout/BasePanelBox';
import BaseSheet from '../Common/Layout/BaseSheet';
import { StyledPanelHeader } from '../Common/Layout/PanelHeader';
import Paging from '../SSOManagement/Common/Paging';

const emptySelectedSite: LPLItemDTO = {
    id: '',
    state: '',
    name: '',
    leaid: '',
    siteName: '',
};

const emptySelectedLocation: LocationDTO = {
    id: -1,
    name: '',
    isoCode: '',
    parentId: -1,
    hasChildren: true
};

const initialLocationsValue: LocationDTO[] = [emptySelectedLocation];
const initialParentLocationsValue: LocationDTO[] = [emptySelectedLocation];
const initialLPLItemsValue: LPLItemDTO[] = [emptySelectedSite];

export default function SiteManagement() {
    const theme = useTheme();
    const classes = styles.pageStyles(theme);
    const [showLocations, setShowLocations] = React.useState(true); //if true, then we are showing locations!
    const [shouldFetchLocations, setShouldFetchLocations] = React.useState(true);
    const [displayAddModal, setDisplayAddModal] = React.useState(false);
    const [locationsPage, setLocationsPage] = React.useState(0);
    const [lpliPage, setLpliPage] = React.useState(0);
    const [locationPages, setLocationsPageCount] = React.useState(0);
    const [lpliPages, setLpliPageCount] = React.useState(0);
    const [locationListItems, setLocationListItems] = React.useState(initialLocationsValue);
    const [parentLocationListItems, setParentLocationListItems] = React.useState(initialParentLocationsValue);
    const [loginPickListItems, setLoginPickListItems] = React.useState(initialLPLItemsValue);
    const [openEditModal, setOpenEditModal] = React.useState(false);
    const [openDeleteAdminModal, setOpenDeleteModal] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState(showLocations ? emptySelectedLocation : emptySelectedSite);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [searchLocationsResult, setSearchLocationResult] = React.useState<LocationDTO[]>([]);
    const [searchSitesResult, setSearchSitesResult] = React.useState<LPLItemDTO[]>([]);
    const [noSearchResults, setNoSearchResults] = React.useState(false);
    const [isLoading, setIsloading] = React.useState(false);

    const clearSearchResults = () => {
        setSearchLocationResult([]);
        setSearchSitesResult([]);
        setSearchTerm('');
    }

    const resetSearchOnSuccess = () => {
        if (searchTerm !== '') {
            clearSearchResults();
        }
    }

    const openAddLocationModal = () => setDisplayAddModal(true);
    const closeAddModal = (added: boolean) => {
        setDisplayAddModal(false);
        if (added) {
            resetSearchOnSuccess();
            setShouldFetchLocations(true);
        }
    }

    const flipShowLocations = () => {
        setShowLocations(!showLocations);
        setSelectedItem(!showLocations ? emptySelectedSite : emptySelectedLocation)
        setNoSearchResults(false);
        clearSearchResults();
    }

    const openEdtModal = () => setOpenEditModal(true);
    const closeEditModal = (edited: boolean) => {
        setOpenEditModal(false);
        if (edited) {
            resetSearchOnSuccess();
            setSelectedItem(showLocations ? emptySelectedLocation : emptySelectedSite);
            setShouldFetchLocations(true);
        }
    }

    const openDeleteModal = () => setOpenDeleteModal(true);
    const closeDeleteModal = (deleted: boolean) => {
        setOpenDeleteModal(false);
        if (deleted) {
            resetSearchOnSuccess();
            setSelectedItem(showLocations ? emptySelectedLocation : emptySelectedSite);
            setShouldFetchLocations(true);
        }
    }

    const handleTableRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
        e.persist();
        e.stopPropagation();

        const selectedRow = e.currentTarget;

        const selItem =
            (showLocations)
                ? getLocationItems().find((lli: LocationDTO) => lli?.id && lli.id.toString() === selectedRow.id)
                : getPickListItems().find((lpli: LPLItemDTO) => lpli.id.toString() === selectedRow.id);

        if (selItem && selItem.id !== selectedItem.id) {
            setSelectedItem(selItem);
        } else {
            setSelectedItem(showLocations ? emptySelectedLocation : emptySelectedSite);
        }
    }

    const getLPLItems = useCallback(async () => {
        setIsloading(true);
        const lplItemResponse = await siteManagementService.getLPLItems(lpliPage);
        if (lplItemResponse) {
            setLoginPickListItems(lplItemResponse.data);
            setLpliPageCount(lplItemResponse.pages);
        }
        setIsloading(false);

    }, [lpliPage]);

    const getAllLocations = useCallback(async () => {
        setIsloading(true);
        const locationsResponse = await siteManagementService.getAllLocations(locationsPage);
        if (locationsResponse) {
            setLocationListItems(locationsResponse.data);
            setLocationsPageCount(locationsResponse.pages);
        }
        setIsloading(false);

    }, [locationsPage]);

    useEffect(() => {
        if (shouldFetchLocations) {
            getAllLocations();
            setShouldFetchLocations(false);
        }
    }, [shouldFetchLocations, getAllLocations]);

    useEffect(() => {
        getLPLItems();
    }, [getLPLItems]);

    useEffect(() => {
        const getAllParentLocations = async () => {
            setIsloading(true);
            const locationsResponse = await siteManagementService.getAllLocations(0, 0);
            if (locationsResponse) {
                setParentLocationListItems(locationsResponse.data);
            }

            setIsloading(false);
        }

        if (shouldFetchLocations) {
            getAllParentLocations();
            setShouldFetchLocations(false);
        }

    }, [getAllLocations, shouldFetchLocations]);

    const handleSearch = async () => {
        setNoSearchResults(false);
        setIsloading(true);

        if (showLocations && searchTerm !== "") {
            const searchResult = await siteManagementService.searchLocations(searchTerm);
            if (searchResult && searchResult.data.length === 0) setNoSearchResults(true);
            else {
                setSearchLocationResult(searchResult ? searchResult.data : initialLocationsValue);
            }
        } else if (!showLocations && searchTerm !== "") {
            const searchResult = await siteManagementService.searchLPLItems(searchTerm);
            if (searchResult && searchResult.data.length === 0) setNoSearchResults(true);
            else {
                setSearchSitesResult(searchResult ? searchResult.data : initialLPLItemsValue);
            }
        }
        setIsloading(false);
    }

    const handleKeyPress = (e: any) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSeachTermChange = (e: any) => {
        setSearchTerm(e.target.value);
        if (e.target.value === '') {
            clearSearchResults();
        }
    };

    const getParentLocation = (item: LocationDTO) => {
        if (item.parentId === 1) return 'United States';
        if (item.parentId === 0) return 'N/A';

        const parentLocation = locationListItems.find((lli) => lli.id === item.parentId);
        return parentLocation?.name ?? 'N/A';
    };

    const getLocationItems = () => {
        if (searchLocationsResult.length) {
            return searchLocationsResult;
        }

        return locationListItems;
    }

    const getPickListItems = () => {
        if (searchSitesResult.length) {
            return searchSitesResult;
        }

        return loginPickListItems;
    }

    return (
        <>
            <Header />
            <Grid container>
                <BasePanel>
                    <BasePanelBox>
                        <StyledPanelHeader header="Site Management" />
                        <Grid container size={12}>
                            <Typography variant="h5" sx={{ marginLeft: '16px' }}>General Options</Typography>
                            <Container sx={classes.dfb}>
                                <Button sx={classes.verticalBtn} variant="contained" color="primary" onClick={openAddLocationModal}>{showLocations ? 'Add new location' : 'Add new site'}</Button>
                            </Container>
                            {selectedItem?.name ? (
                                <>
                                    <Typography variant="h5" sx={{ marginLeft: '16px' }}>Options {selectedItem.name !== '' ? `for ${selectedItem.name}` : ''}</Typography>
                                    <Container sx={classes.dfb}>
                                        <Button sx={classes.verticalBtn} variant="contained" color="secondary" onClick={openEdtModal}>Edit</Button>
                                        {!((selectedItem as LocationDTO).hasChildren) || !showLocations ?
                                            <Button sx={[classes.verticalBtn, classes.plainBtn]} variant="contained" onClick={openDeleteModal}>Delete</Button>
                                            : undefined}
                                    </Container>
                                </>
                            ) : undefined}
                        </Grid>
                    </BasePanelBox>
                </BasePanel>
                <BaseSheet>
                    <Grid container size={12}>
                        <Typography align-left='true' sx={{
                            marginBottom: "16px"
                        }}>Use this page to manage locations and sites within the Beable login home page.</Typography>

                        <Container sx={classes.dfb}>
                            <Button sx={classes.toggleBtn} variant="outlined" color="secondary" onClick={flipShowLocations}>Show {showLocations ? "Sites" : "Locations"}</Button>
                            <TextField
                                type="search"
                                variant="outlined"
                                margin="normal"
                                onKeyPress={handleKeyPress}
                                onChange={handleSeachTermChange}
                                value={searchTerm}
                                placeholder={showLocations ? "Type in a search term for locations and hit enter." : "Type in a search term for sites and hit enter."}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                            {noSearchResults ? <Typography sx={classes.mb2}>No results found.</Typography> : undefined}
                            <Paging
                                filteredResultsLength={showLocations ? searchLocationsResult.length : searchSitesResult.length}
                                totalPages={showLocations ? locationPages : lpliPages}
                                setPage={showLocations ? setLocationsPage : setLpliPage}
                            />
                        </Container>
                        <Container sx={classes.dfb}>
                            {isLoading ? <CircularProgress color="primary" /> : (
                                <TableContainer component={Paper} sx={classes.maxHeight}>
                                    {showLocations ? (
                                        <Table sx={classes.table} aria-label="locations data table" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="left">Name</TableCell>
                                                    <TableCell align="left">ISO&nbsp;Code</TableCell>
                                                    <TableCell align="left">Parent&nbsp;Location</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    getLocationItems().map(lli => (
                                                        <TableRow sx={selectedItem?.id && selectedItem.id.toString() === lli.id!.toString() ? classes.highlight : undefined} id={lli.id!.toString()} key={lli.id} onClick={handleTableRowClick}>
                                                            <TableCell align="left">{lli.name}</TableCell>
                                                            <TableCell align="left">{lli.isoCode}</TableCell>
                                                            <TableCell align="left">{getParentLocation(lli)}</TableCell>
                                                        </TableRow>
                                                    ))
                                                }
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <Table sx={classes.table} aria-label="sites data table" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="left">Name</TableCell>
                                                    <TableCell align="left">Location</TableCell>
                                                    <TableCell align="left">Site&nbsp;Name</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    getPickListItems().map(lpli => (
                                                        <TableRow sx={selectedItem?.id && selectedItem.id.toString() === lpli.id.toString() ? classes.highlight : undefined} id={lpli.id.toString()} key={lpli.id} onClick={handleTableRowClick}>
                                                            <TableCell align="left">{lpli.name}</TableCell>
                                                            <TableCell align="left">{lpli.state}</TableCell>
                                                            <TableCell align="left">{lpli.siteName}</TableCell>
                                                        </TableRow>
                                                    ))
                                                }
                                            </TableBody>
                                        </Table>
                                    )}
                                </TableContainer>
                            )}
                        </Container>
                    </Grid>
                </BaseSheet>
            </Grid>
            <AddModal open={displayAddModal} handleClose={closeAddModal} addingLocation={showLocations} parentLocations={parentLocationListItems}
                locations={locationListItems} />
            <EditModal open={openEditModal} handleClose={closeEditModal} editingLocation={showLocations}
                locationItem={showLocations ? selectedItem as LocationDTO : emptySelectedLocation}
                siteItem={!showLocations ? selectedItem as LPLItemDTO : emptySelectedSite}
                parentLocations={parentLocationListItems} locations={locationListItems}
            />
            <DeleteModal open={openDeleteAdminModal} handleClose={closeDeleteModal} deletingLocation={showLocations} item={selectedItem} />
        </>
    );
}